import mqtt from 'mqtt';
import type { RealtimeConfig } from './config.ts';
import type { RealtimeSubscription, RealtimeTransport } from './transport.ts';

// MQTT-over-WebSocket transport to AWS IoT Core (PRD-0006 D10). Authenticates
// with the custom authorizer Lambda by presenting the Cognito ID token — the
// authorizer verifies it (same crypto as the REST plane) and returns a tenant-
// scoped, subscribe-only policy. The browser can only ever RECEIVE under its own
// `fleet/{tenant}/…`; publish is denied at the broker, so commands stay REST-only.
//
// Enhanced custom auth with token-signing disabled: the authorizer name rides in
// the WebSocket URL query string, and the token rides in the MQTT CONNECT
// password. IoT invokes the authorizer with that token and caches the resulting
// policy per connection (TTL ~5 min — the revocation-latency bound documented in
// the PRD's "Realtime tenant security").
//
// mqtt.js owns reconnect/backoff; we surface only connected/message to the hook.

export class MqttTransport implements RealtimeTransport {
  constructor(private readonly cfg: RealtimeConfig) {}

  subscribe(topicFilters: string[], sub: RealtimeSubscription): () => void {
    const url = this.connectUrl();
    const client = mqtt.connect(url, {
      protocolVersion: 5,
      // A random client id per tab; the authorizer policy binds Connect to
      // ${iot:ClientId}, so any id works and two tabs don't collide.
      clientId: `fleet-web-${Math.random().toString(16).slice(2)}`,
      // Enhanced custom auth (token-signing disabled): the Cognito ID token is
      // presented as the CONNECT password; the authorizer name is in the URL.
      // Username is non-empty so the MQTT password flag is definitely set (some
      // clients/brokers drop the password when the username is blank).
      username: 'fleet-web',
      password: this.cfg.getToken() ?? '',
      reconnectPeriod: 5000,
      clean: true,
    });

    client.on('connect', () => {
      // "Connected" means the SUBSCRIPTION is live, not just the socket: IoT
      // answers an unauthorized filter with a denied SUBACK (reason >= 0x80)
      // and mqtt.js otherwise swallows it — the old code showed "Live
      // updates" while receiving nothing (the policy-wildcard bug this
      // callback now guards against).
      client.subscribe(topicFilters, { qos: 1 }, (err, granted) => {
        const denied =
          err != null ||
          !granted ||
          granted.length === 0 ||
          granted.every((g) => g.qos >= 0x80);
        if (denied) {
          console.warn(
            'realtime: subscription denied/failed; falling back to polling',
            { topicFilters, err, granted },
          );
        }
        sub.onConnectedChange?.(!denied);
      });
    });
    client.on('reconnect', () => {
      // Refresh the token before each reconnect: the original expires (~60 min)
      // and oidc-client-ts renews it in storage. Without this, reconnects reuse
      // the stale expired token and the authorizer denies forever.
      const fresh = this.cfg.getToken();
      if (fresh) client.options.password = fresh;
      sub.onConnectedChange?.(false);
    });
    client.on('close', () => sub.onConnectedChange?.(false));
    client.on('error', () => sub.onConnectedChange?.(false));

    client.on('message', (topic: string, raw: Uint8Array) => {
      let payload: unknown;
      try {
        payload = JSON.parse(new TextDecoder().decode(raw));
      } catch {
        payload = undefined; // non-JSON (e.g. a future lidar frame): raw only
      }
      sub.onMessage({ topic, payload, raw });
    });

    return () => {
      try {
        client.end(true);
      } catch {
        // best-effort teardown
      }
      sub.onConnectedChange?.(false);
    };
  }

  // wss URL for IoT custom-auth. The authorizer name is the query param IoT
  // reads to route the CONNECT to our Lambda.
  private connectUrl(): string {
    const name = encodeURIComponent(this.cfg.authorizerName);
    return `wss://${this.cfg.endpoint}/mqtt?x-amz-customauthorizer-name=${name}`;
  }
}
